import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient, ApiError } from '../config/api-client';
import { queryKeys } from '../config/query-keys';
import type { Channel, ExploreData, PaginatedResponse, PopularChannel } from '../types';

export function useChannelsQuery(options?: {
  readonly enabled?: boolean;
  readonly country?: string | null;
  readonly limit?: number;
}) {
  const country = options?.country ?? null;
  const limit = options?.limit ?? 24;
  return useQuery({
    queryKey: country
      ? [...queryKeys.channels.all, 'country', country.toUpperCase(), 'limit', limit]
      : [...queryKeys.channels.all, 'limit', limit],
    queryFn: async () => {
      const qp = new URLSearchParams({ limit: String(limit) });
      if (country) qp.set('country', country.toUpperCase());
      const result = await apiClient.get<{ data: PaginatedResponse<Channel> | Channel[] }>(
        `/channels?${qp.toString()}`,
      );
      // Server returns paginated for /channels and array for some legacy
      // endpoints — handle both shapes defensively.
      const payload = result.data.data;
      return Array.isArray(payload) ? payload : payload.items;
    },
    enabled: options?.enabled ?? true,
  });
}

export function useSubscribedChannelsQuery(options?: { readonly enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.channels.subscribed,
    queryFn: async () => {
      const result = await apiClient.get<{ data: PaginatedResponse<Channel> | Channel[] }>(
        '/channels/subscribed?page=1&limit=20',
      );
      const payload = result.data.data;
      return Array.isArray(payload) ? payload : payload.items;
    },
    enabled: options?.enabled ?? true,
  });
}

// ---------------------------------------------------------------------------
// Explore (for-you) — local + home + world + popular sections in one call.
// ---------------------------------------------------------------------------

export function useExploreDataQuery(options?: { readonly enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.channels.explore,
    queryFn: async () => {
      const result = await apiClient.get<{ data: ExploreData }>('/channels/for-you');
      return result.data.data;
    },
    staleTime: 15 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}

// ---------------------------------------------------------------------------
// Popular channels filtered by country.
// ---------------------------------------------------------------------------

export function usePopularChannelsQuery(country: string | null, limit = 10) {
  return useQuery<PopularChannel[]>({
    queryKey: queryKeys.channels.popular(country),
    queryFn: async () => {
      const qp: string[] = [`limit=${limit}`];
      if (country) qp.push(`country=${encodeURIComponent(country)}`);
      const result = await apiClient.get<{ data: PopularChannel[] }>(
        `/channels/popular?${qp.join('&')}`,
      );
      return result.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Channel search — gated on >= 2 chars.
// ---------------------------------------------------------------------------

export function useChannelSearchQuery(query: string) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: queryKeys.channels.search(trimmed),
    queryFn: async () => {
      const result = await apiClient.get<{ data: PaginatedResponse<Channel> }>(
        `/channels/search?q=${encodeURIComponent(trimmed)}`,
      );
      return result.data.data.items;
    },
    enabled: trimmed.length >= 2,
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Single channel detail.
// ---------------------------------------------------------------------------

export function useChannelByIdQuery(channelId: string | null) {
  return useQuery({
    queryKey: queryKeys.channels.detail(channelId ?? ''),
    queryFn: async () => {
      const result = await apiClient.get<{ data: Channel }>(
        `/channels/${encodeURIComponent(channelId!)}`,
      );
      return result.data.data;
    },
    enabled: !!channelId,
  });
}

// ---------------------------------------------------------------------------
// Follow / Unfollow — aligns to mobile's /follow path. Mobile-style 409 = OK
// because "already following" is the desired state.
// ---------------------------------------------------------------------------

export function useFollowChannelMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (channelId: string) => {
      try {
        await apiClient.post(`/channels/${encodeURIComponent(channelId)}/follow`);
      } catch (err) {
        if (err instanceof ApiError && err.statusCode === 409) return;
        throw err;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.channels.subscribed });
      void queryClient.invalidateQueries({ queryKey: queryKeys.feed.list() });
    },
  });
}

export function useUnfollowChannelMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (channelId: string) => {
      await apiClient.delete(`/channels/${encodeURIComponent(channelId)}/follow`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.channels.subscribed });
      void queryClient.invalidateQueries({ queryKey: queryKeys.feed.list() });
    },
  });
}

// Onboarding still uses the older /subscribe path (ergonomically identical
// on the server); a future cleanup can collapse the two.
export function useToggleChannelSubscribe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { readonly channelId: string; readonly subscribe: boolean }) => {
      const path = `/channels/${encodeURIComponent(input.channelId)}/subscribe`;
      if (input.subscribe) {
        try {
          await apiClient.post(path);
        } catch (err) {
          if (err instanceof ApiError && err.statusCode === 409) return;
          throw err;
        }
      } else {
        await apiClient.delete(path);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.channels.subscribed });
      void queryClient.invalidateQueries({ queryKey: queryKeys.feed.list() });
    },
  });
}
